import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Tab, Theme } from '@mui/material';
import { Nullable } from 'common-types';
import { FC, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import {
  MdLogin,
  MdOutlineNotificationsActive,
  MdOutlineVpnKey,
} from 'react-icons/md';
import { VscOrganization } from 'react-icons/vsc';
import { useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';
import { AppPages, navigate } from '../../common/routing/app-navigator';
import { Organization } from '../../organizations/organization-types';
import { setCurrentTabFromURL } from '../actions/settings-actions';
import OrgSettingsTab from '../components/OrgSettingsTab';
import { setRemoveOrgModalVisibility } from '../store-actions/org-settings-store-actions';
import SettingsStore from '../stores/settings-store';
import { TabQueryParam } from '../types/settings-types';
import ApiKeySettingsTab from './ApiKeySettingsTab';
import ConnectionSettingsTab from './ConnectionSettingsTab';
import NotificationSettingsTab from './NotificationSettingsTab';
import OxFooter from './OxFooter';

const Settings: FC<React.PropsWithChildren<SettingsProps>> = ({
  chosenOrganization,
  isUserAdminRole,
}) => {
  const { classes } = useStyles();
  const { currentTab } = useSnapshot(SettingsStore);
  const [settingsTabValue, setSettingsTabValue] = useState<TabQueryParam>(
    TabQueryParam.OrgSettings,
  );

  const location = useLocation();

  useEffect(() => {
    setCurrentTabFromURL(location.search);
  }, [location]);

  useEffect(() => {
    setSettingsTabValue(currentTab);
  }, [currentTab]);

  const showRemoveOrgModal = useCallback(() => {
    setRemoveOrgModalVisibility(true);
  }, []);

  const handleChange = (_event: SyntheticEvent, newValue: TabQueryParam) => {
    navigate(AppPages.Settings, `tab=${newValue}`);
  };

  return (
    <div className={classes.settingsContainer}>
      <TabContext value={settingsTabValue}>
        <TabList
          onChange={handleChange}
          value={settingsTabValue}
          className={classes.tabIcon}>
          <Tab
            icon={<VscOrganization />}
            iconPosition='start'
            value={TabQueryParam.OrgSettings}
            label={SettingsContainerTabLabel.OrgSettings}
          />
          <Tab
            icon={<MdLogin />}
            iconPosition='start'
            value={TabQueryParam.LoginSettings}
            label={SettingsContainerTabLabel.LoginSettings}
          />
          <Tab
            icon={<MdOutlineNotificationsActive />}
            iconPosition='start'
            value={TabQueryParam.NotificationSettings}
            label={SettingsContainerTabLabel.NotificationSettings}
          />
          <Tab
            icon={<MdOutlineVpnKey />}
            iconPosition='start'
            value={TabQueryParam.ApiKeySettings}
            label={SettingsContainerTabLabel.ApiKeySettings}
          />
        </TabList>

        <div className={classes.tabPanelContainer}>
          <TabPanel
            className={classes.tabPanel}
            value={TabQueryParam.OrgSettings}>
            <OrgSettingsTab
              showRemoveOrgModal={showRemoveOrgModal}
              isUserAdminRole={isUserAdminRole}
              chosenOrganization={chosenOrganization}
            />
          </TabPanel>
          <TabPanel
            className={classes.tabPanel}
            value={TabQueryParam.LoginSettings}>
            <ConnectionSettingsTab
              showRemoveOrgModal={showRemoveOrgModal}
              isUserAdminRole={isUserAdminRole}
              chosenOrganization={chosenOrganization}
            />
          </TabPanel>
          <TabPanel
            className={classes.tabPanel}
            value={TabQueryParam.NotificationSettings}>
            <NotificationSettingsTab
              showRemoveOrgModal={showRemoveOrgModal}
              isUserAdminRole={isUserAdminRole}
              chosenOrganization={chosenOrganization}
            />
          </TabPanel>
          <TabPanel
            className={classes.tabPanel}
            value={TabQueryParam.ApiKeySettings}>
            <ApiKeySettingsTab
              showRemoveOrgModal={showRemoveOrgModal}
              isUserAdminRole={isUserAdminRole}
              chosenOrganization={chosenOrganization}
            />
          </TabPanel>
        </div>
      </TabContext>
      <OxFooter />
    </div>
  );
};

enum SettingsContainerTabLabel {
  OrgSettings = 'Organization Settings',
  LoginSettings = 'Login Settings',
  NotificationSettings = 'Notification Settings',
  ApiKeySettings = 'API Key Settings',
  AuditLogs = 'Audit Logs',
}
interface SettingsProps {
  isReadOnlyUser: boolean;
  chosenOrganization: Nullable<Organization>;
  isUserAdminRole: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  settingsContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabIcon: {
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& svg': {
      height: '1em',
      width: 'auto',
    },
  },
  tabPanel: {
    height: '100%',
    minHeight: 300,
  },
  scrollable: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
  },
  tabPanelContainer: {
    flex: '1 1 auto',
    overflow: 'hidden',
    minHeight: 300,
  },
}));

export default Settings;
